import { useWorkerMenuPriviligesProviderContext } from "../priviligesProviders/WorkerMenuPriviligesProvider";
import { Redirect, Route, Switch } from "react-router-dom";
import { loadLazily } from "@src/utils/loadLazily";
import { routes } from "@src/appV2/redesign/utils/routes";
import { PrivateRoute } from "@src/components/routing";
import { useSession } from "@src/appV2/Auth/api/useSession";

const AgentDashboard = loadLazily(() =>
  import("@src/containers/agentDashboard").then((module) => ({
    default: module.AgentDashboard,
  }))
);

const ChatPage = loadLazily(() =>
  import("@src/containers/chat/chat").then((module) => ({
    default: module.ChatPage,
  }))
);

const Payroll = loadLazily(() => import("@src/containers/payroll/payrollView"));

const StripeTransfers = loadLazily(
  () => import("@src/containers/payroll/stripeTransfers/stripeTransfers")
);

const StripePayouts = loadLazily(
  () => import("@src/containers/payroll/stripePayouts/stripePayouts")
);

const AttendanceScore = loadLazily(() => import("@src/containers/attendanceScore"));

const AdminPlacementCandidatePage = loadLazily(() =>
  import("@src/appV2/PlacementCandidates/AdminPlacementCandidatePage").then((module) => ({
    default: module.AdminPlacementCandidatePage,
  }))
);

const AgentExclusionsPage = loadLazily(() =>
  import("@src/appV2/PlacementCandidates/PlacementCandidateExclusionsPage").then((module) => ({
    default: module.PlacementCandidateExclusionsPage,
  }))
);

export function WorkerRoutes(): JSX.Element {
  const workerMenuPriviliges = useWorkerMenuPriviligesProviderContext();

  const { admin, type } = useSession();
  const isWorker = type === "AGENT";

  // isAgent should only be true if the user is a worker AND not an admin
  // This ensures admins pseudo-logged into a worker account can still view worker pages
  // instead of being redirected to the mobile app (AgentExperience)
  const shouldRedirectToMobileApp = !admin && isWorker;

  return (
    <Switch>
      <PrivateRoute
        exact
        path={routes.agentDashboard}
        location={location}
        component={AgentDashboard}
        isAuthorized={workerMenuPriviliges.dashboard}
        isAgent={shouldRedirectToMobileApp}
      />

      <PrivateRoute
        path={routes.facilityChat}
        component={ChatPage}
        isAuthorized={workerMenuPriviliges.chat}
        isAgent={shouldRedirectToMobileApp}
      />

      <PrivateRoute
        path={routes.agentPayroll}
        component={Payroll}
        isAuthorized={workerMenuPriviliges.payrollSection?.payroll}
        isAgent={shouldRedirectToMobileApp}
      />

      <PrivateRoute
        path={routes.agentStripeTransfers}
        component={StripeTransfers}
        isAuthorized={workerMenuPriviliges.payrollSection?.stripeTransfers}
        isAgent={shouldRedirectToMobileApp}
      />

      <PrivateRoute
        path={routes.agentStripePayouts}
        component={StripePayouts}
        isAuthorized={workerMenuPriviliges.payrollSection?.stripePayouts}
        isAgent={shouldRedirectToMobileApp}
      />

      <PrivateRoute
        path={routes.agentAttendance}
        component={AttendanceScore}
        isAuthorized={workerMenuPriviliges.clipboardScore}
        isAgent={shouldRedirectToMobileApp}
      />

      <PrivateRoute
        path={routes.agentPlacement}
        component={AdminPlacementCandidatePage}
        isAuthorized={workerMenuPriviliges.placementSection?.candidateProfile}
        isAgent={shouldRedirectToMobileApp}
      />

      <PrivateRoute
        path={routes.agentPlacementCandidateExclusions}
        component={AgentExclusionsPage}
        isAuthorized={workerMenuPriviliges.placementSection?.exclusions}
        isAgent={shouldRedirectToMobileApp}
      />

      {/* Catch-all: redirect unrecognized worker routes to the default worker page */}
      <Route>
        <Redirect to={routes.agentDashboard} />
      </Route>
    </Switch>
  );
}
